import { BigNumber, utils } from "ethers";

/**
 * A finite number as a plain decimal string (FE-16).
 *
 * `String(1e-7)` is `"1e-7"` and `String(1e21)` is `"1e+21"`; downstream
 * formatters and `currency.js` read those as garbage or zero. This expands the
 * exponent instead. Non-finite input gives `"0"`.
 */
export const toPlainDecimalString = (value: number): string => {
  if (!Number.isFinite(value)) return "0";
  const text = String(value);
  if (!/e/i.test(text)) return text;

  const negative = value < 0;
  const [mantissa, exponentText] = String(Math.abs(value)).toLowerCase().split("e");
  const exponent = Number(exponentText);
  const pointIndex = mantissa.includes(".") ? mantissa.indexOf(".") : mantissa.length;
  const digits = mantissa.replace(".", "");
  const newPoint = pointIndex + exponent;

  // JS prints an exponent only below 1e-6 (point moves left of every digit)
  // or from 1e21 up (point moves right past every significant digit), so
  // there is no case with the point landing inside the digits.
  const plain =
    newPoint <= 0
      ? `0.${"0".repeat(-newPoint)}${digits}`
      : `${digits}${"0".repeat(newPoint - digits.length)}`;
  return negative ? `-${plain}` : plain;
};

const NUMERIC_TEXT = /^-?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i;
const MAX_DECIMAL_EXPONENT = 400;

/**
 * Canonical plain decimal text for a number or numeric string, or `null` when it
 * is not one (`"1,000"`, `"1.5abc"`, `""`, NaN).
 *
 * Strings are expanded WITHOUT going through a float, so an 18-decimal balance
 * such as "1.123456789012345678" keeps every digit (`Number` would round it UP by
 * 22 wei). `"1.48e-6"` becomes `"0.00000148"`, `".5"` becomes `"0.5"`.
 */
export const normalizeDecimalString = (
  value: string | number | null | undefined
): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? toPlainDecimalString(value) : null;
  }
  const text = value.trim();
  if (!NUMERIC_TEXT.test(text)) return null;

  const negative = text.startsWith("-");
  const [mantissa, exponentText] = text.replace(/^-/, "").toLowerCase().split("e");
  const exponent = Number(exponentText || 0);
  // No real amount has an exponent this large; expanding "1e999999999" would
  // throw a RangeError from `repeat` instead of reporting "not a number".
  if (Math.abs(exponent) > MAX_DECIMAL_EXPONENT) return null;
  const [intPart = "", fracPart = ""] = mantissa.split(".");
  const digits = `${intPart}${fracPart}`;
  const point = intPart.length + exponent;

  let whole: string;
  let fraction: string;
  if (point <= 0) {
    whole = "0";
    fraction = `${"0".repeat(-point)}${digits}`;
  } else if (point >= digits.length) {
    whole = `${digits}${"0".repeat(point - digits.length)}`;
    fraction = "";
  } else {
    whole = digits.slice(0, point);
    fraction = digits.slice(point);
  }
  whole = whole.replace(/^0+(?=\d)/, "") || "0";
  fraction = fraction.replace(/0+$/, "");
  const plain = fraction ? `${whole}.${fraction}` : whole;
  return negative && plain !== "0" ? `-${plain}` : plain;
};

/**
 * `value` cut to at most `decimals` places. For non-negative values this is a
 * FLOOR, never a round-up, so the result can never exceed the balance, position
 * or step it came from. Invalid input gives `null`.
 */
export const truncateDecimalString = (
  value: string | number | null | undefined,
  decimals: number
): string | null => {
  const plain = normalizeDecimalString(value);
  if (plain === null) return null;
  const [whole, fraction = ""] = plain.split(".");
  const cut = fraction.slice(0, Math.max(0, decimals)).replace(/0+$/, "");
  const result = cut ? `${whole}.${cut}` : whole;
  return result === "-0" ? "0" : result;
};

/**
 * A copyable figure (balance, position, Max, "available") floored to exactly
 * `decimals` places: 0.00000148 at 7dp shows "0.0000014", never "0.0000015",
 * which a trader would type back in and be rejected for.
 *
 * CALLER BEWARE, twice:
 *
 * 1. Invalid or ABSENT input gives "0.00…". That is deliberate -- this returns a
 *    string, and every caller today passes a figure it already knows exists --
 *    but it means an unknown balance is indistinguishable from an empty one.
 *    Check for the unknown BEFORE calling (AssetsTab and SpotDepositWithdraw
 *    both do); do not let a null reach here and read the result as a zero.
 *
 * 2. `truncateDecimalString` cuts TOWARD ZERO, so for a negative value this is
 *    not a floor: -1.239 at 2dp is "-1.23". Every caller today passes a
 *    non-negative balance, where toward-zero IS the floor and is what the
 *    "never exceed what it came from" guarantee means. Toward-zero is also the
 *    correct direction for the on-chain size arguments `truncateDecimalString`
 *    builds, for either sign -- a close size must never exceed the position.
 *    But a SIGNED DISPLAY value (a PnL, a delta) must not come through here:
 *    it would shave the loss in the trader's favour.
 */
export const floorToDecimalsString = (
  value: string | number | null | undefined,
  decimals: number
): string => {
  const dp = Math.max(0, Math.floor(decimals));
  const cut = truncateDecimalString(value, dp) ?? "0";
  if (dp === 0) return cut;
  const [whole, fraction = ""] = cut.split(".");
  return `${whole}.${fraction.padEnd(dp, "0")}`;
};

/**
 * A user-entered amount as token base units, floored to the token's decimals.
 * `null` for anything `parseUnits` would throw on or that is negative, so callers
 * can show a validation message instead of a raw ethers error.
 */
export const toTokenUnits = (
  value: string | number | null | undefined,
  decimals: number
): BigNumber | null => {
  const cut = truncateDecimalString(value, decimals);
  if (cut === null || cut.startsWith("-")) return null;
  try {
    return utils.parseUnits(cut, decimals);
  } catch {
    return null;
  }
};

/**
 * Validator for a typed amount: plain digits with at most one dot (no sign, no
 * exponent, no thousands separator) and, when given, no more than `maxDecimals`
 * places.
 */
export const isValidDecimalInput = (
  value: string | number | null | undefined,
  maxDecimals?: number
): boolean => {
  const text = String(value ?? "").trim();
  if (!/^(\d+\.?\d*|\.\d+)$/.test(text)) return false;
  if (maxDecimals === undefined) return true;
  return (text.split(".")[1]?.length ?? 0) <= maxDecimals;
};
